import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MainNavbar } from '../../../components/navigation/MainNavbar';
import { usePublicAuth } from '../../../contexts/PublicAuthContext.jsx';
import {
  CalendarDays,
  MapPin,
  Package,
  Box,
  ArrowLeft,
  ArrowRight,
  Car,
  Ship,
  Plane,
  DollarSign,
  FileText,
  ListChecks,
  Route,
  Truck,
  CheckCircle,
  ChevronUp,
  ChevronDown,
} from 'lucide-react';
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { PlacesAutocompleteInput } from '../../../components/inputs/PlacesAutocompleteInput';

const formatDate = date => {
  if (!date) return '';
  const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
  const formattedDate = date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return `${dayName}, ${formattedDate}`;
};

const formatTime = date => {
  if (!date) return '';
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

export default function ListNewTrip() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = usePublicAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [tripId, setTripId] = useState(null);

  const [formData, setFormData] = useState({
    originAddress: '',
    destinationAddress: '',
    originDetails: null,
    destinationDetails: null,
    departureDate: '',
    departureTime: '',
    deliveryMethod: 'byRoad',
    availableSpace: '',
    spaceUnit: 'kg',
    pricingMode: 'perUnit',
    pricePerUnit: '',
    flatPrice: '',
    notes: '',
    contactViaApp: false,
    acceptPolicy: false,
  });

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTime, setSelectedTime] = useState(new Date());
  const [draftDate, setDraftDate] = useState(new Date());
  const [draftTime, setDraftTime] = useState(new Date());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [showDeliveryMethodModal, setShowDeliveryMethodModal] = useState(false);

  const steps = [
    'route',
    'departure',
    'deliveryMethod',
    'capacity',
    'pricing',
    'additionalInfo',
    'agreements',
    'summary',
  ];
  const totalSteps = steps.length;

  const tripHeroHighlights = [
    { label: t('listNewTrip.avgEarningsPerTrip'), value: '$85+' },
    { label: t('listNewTrip.communitySwaps'), value: '18k+' },
    { label: t('listNewTrip.routesListedWeekly'), value: '640+' },
  ];

  const spaceUnits = [
    { label: 'kg (kilograms)', value: 'kg' },
    { label: 'lb (pounds)', value: 'lb' },
    { label: 'oz (ounces)', value: 'oz' },
    { label: 'pieces', value: 'pieces' },
    { label: 'meters', value: 'meters' },
    { label: 'cm (centimeters)', value: 'centimeters' },
    { label: 'liters (L)', value: 'liters' },
    { label: 'gallons (US)', value: 'gallons_us' },
    { label: 'cubic meters (m³)', value: 'cubic_meters' },
    { label: 'cubic feet (ft³)', value: 'cubic_feet' },
    { label: 'cubic inches (in³)', value: 'cubic_inches' },
  ];

  const quickDateOptions = useMemo(
    () => [
      { label: t('listNewTrip.today'), offsetDays: 0 },
      { label: t('listNewTrip.tomorrow'), offsetDays: 1 },
      { label: t('listNewTrip.in3Days'), offsetDays: 3 },
      { label: t('listNewTrip.nextWeek'), offsetDays: 7 },
    ],
    [t],
  );

  const quickTimeOptions = useMemo(
    () => ['08:00', '12:00', '15:00', '20:00'],
    [],
  );
  const minuteStep = 1;

  const adjustHour = direction => {
    setDraftTime(prev => {
      const next = new Date(prev);
      next.setHours((next.getHours() + direction + 24) % 24);
      return next;
    });
  };

  const adjustMinute = direction => {
    setDraftTime(prev => {
      const next = new Date(prev);
      next.setMinutes(next.getMinutes() + direction * minuteStep);
      return next;
    });
  };

  const isSameDay = (dateA, dateB) => {
    if (!dateA || !dateB) return false;
    return (
      dateA.getFullYear() === dateB.getFullYear() &&
      dateA.getMonth() === dateB.getMonth() &&
      dateA.getDate() === dateB.getDate()
    );
  };

  const deliveryMethods = [
    { label: t('listNewTrip.byRail'), value: 'byRail', icon: Ship },
    { label: t('listNewTrip.byAir'), value: 'byAir', icon: Plane },
    { label: t('listNewTrip.byRoad'), value: 'byRoad', icon: Car },
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleOriginAddressSelect = location => {
    setFormData(prev => ({
      ...prev,
      originAddress: location?.description || '',
      originDetails: {
        fullAddress: location?.description || '',
        city: location?.description?.split(',')[0] || '',
        country: location?.description?.split(',').pop()?.trim() || '',
        countryFlag: '📍',
        latitude: location?.latitude,
        longitude: location?.longitude,
      },
    }));
  };

  const handleDestinationAddressSelect = location => {
    setFormData(prev => ({
      ...prev,
      destinationAddress: location?.description || '',
      destinationDetails: {
        fullAddress: location?.description || '',
        city: location?.description?.split(',')[0] || '',
        country: location?.description?.split(',').pop()?.trim() || '',
        countryFlag: '📍',
        latitude: location?.latitude,
        longitude: location?.longitude,
      },
    }));
  };

  const validateCurrentStep = () => {
    const step = steps[currentStep];
    if (!step) return true;

    switch (step) {
      case 'route':
        if (!formData.originAddress || !formData.destinationAddress) {
          alert(t('listNewTrip.pleaseSelectBothAddresses'));
          return false;
        }
        if (formData.originAddress === formData.destinationAddress) {
          alert(t('listNewTrip.originDestinationCannotBeSame'));
          return false;
        }
        return true;

      case 'departure':
        if (!formData.departureDate || !formData.departureTime) {
          alert(t('listNewTrip.pleaseSelectDateAndTime'));
          return false;
        }
        return true;

      case 'deliveryMethod':
        if (!formData.deliveryMethod) {
          alert(t('listNewTrip.pleaseSelectDeliveryMethod'));
          return false;
        }
        return true;

      case 'capacity':
        const space = parseFloat(formData.availableSpace);
        if (!space || space <= 0) {
          alert(t('listNewTrip.pleaseEnterValidSpace'));
          return false;
        }
        return true;

      case 'pricing':
        if (formData.pricingMode === 'perUnit') {
          const price = parseFloat(formData.pricePerUnit);
          if (!price || price <= 0) {
            alert(t('listNewTrip.pleaseEnterValidPricePerUnit'));
            return false;
          }
        } else if (formData.pricingMode === 'flat') {
          const price = parseFloat(formData.flatPrice);
          if (!price || price <= 0) {
            alert(t('listNewTrip.pleaseEnterValidFlatPrice'));
            return false;
          }
        }
        return true;

      case 'additionalInfo':
        if (formData.notes && formData.notes.length > 200) {
          alert(t('listNewTrip.notesCannotExceed'));
          return false;
        }
        return true;

      case 'agreements':
        if (!formData.contactViaApp || !formData.acceptPolicy) {
          alert(t('listNewTrip.pleaseAcceptBothAgreements'));
          return false;
        }
        return true;

      case 'summary':
        return true;

      default:
        return true;
    }
  };

  const nextStep = () => {
    if (!validateCurrentStep()) {
      return;
    }
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  useEffect(() => {
    if (showDatePicker) {
      setDraftDate(selectedDate || new Date());
    }
  }, [showDatePicker, selectedDate]);

  useEffect(() => {
    if (showTimePicker) {
      setDraftTime(selectedTime || new Date());
    }
  }, [showTimePicker, selectedTime]);

  // Load trip data for edit mode
  useEffect(() => {
    const state = location.state;
    if (state?.editMode && state?.tripData) {
      const trip = state.tripData;
      setEditMode(true);
      setTripId(trip.id);

      // Parse departure date and time
      let parsedDate = new Date();
      let parsedTime = new Date();
      if (trip.departureDate) {
        // Try to parse the date string (format: "Monday, 11/24/2025")
        const dateMatch = trip.departureDate.match(
          /(\d{1,2})\/(\d{1,2})\/(\d{4})/,
        );
        if (dateMatch) {
          const [, month, day, year] = dateMatch;
          parsedDate = new Date(
            parseInt(year),
            parseInt(month) - 1,
            parseInt(day),
          );
        }
      }
      if (trip.departureTime) {
        // Parse time string (format: "14:02")
        const timeMatch = trip.departureTime.match(/(\d{1,2}):(\d{1,2})/);
        if (timeMatch) {
          const [, hour, minute] = timeMatch;
          parsedTime = new Date();
          parsedTime.setHours(parseInt(hour), parseInt(minute));
        }
      }

      setFormData({
        originAddress:
          trip.originAddress || trip.originDetails?.fullAddress || '',
        destinationAddress:
          trip.destinationAddress || trip.destinationDetails?.fullAddress || '',
        originDetails: trip.originDetails || null,
        destinationDetails: trip.destinationDetails || null,
        departureDate: trip.departureDate || '',
        departureTime: trip.departureTime || '',
        deliveryMethod: trip.deliveryMethod || 'byRoad',
        availableSpace: trip.availableSpace?.toString() || '',
        spaceUnit: trip.spaceUnit || 'kg',
        pricingMode: trip.pricingMode || 'perUnit',
        pricePerUnit: trip.pricePerUnit?.toString() || '',
        flatPrice: trip.flatPrice?.toString() || '',
        notes: trip.notes || '',
        contactViaApp: trip.contactViaApp || false,
        acceptPolicy: trip.acceptPolicy || false,
      });

      setSelectedDate(parsedDate);
      setSelectedTime(parsedTime);
    }
  }, [location.state]);

  const handleDateInputChange = value => {
    if (!value) return;
    const nextDate = new Date(value);
    if (!Number.isNaN(nextDate.getTime())) {
      setDraftDate(nextDate);
    }
  };

  const handleQuickDateSelect = offset => {
    const nextDate = new Date();
    nextDate.setDate(nextDate.getDate() + offset);
    setDraftDate(nextDate);
  };

  const confirmDateSelection = () => {
    setSelectedDate(draftDate);
    handleInputChange('departureDate', formatDate(draftDate));
    setShowDatePicker(false);
  };

  const handleQuickTimeSelect = timeString => {
    const [hourStr, minuteStr] = timeString.split(':');
    const nextTime = new Date(draftTime);
    nextTime.setHours(parseInt(hourStr, 10), parseInt(minuteStr, 10));
    setDraftTime(nextTime);
  };

  const confirmTimeSelection = () => {
    setSelectedTime(draftTime);
    handleInputChange('departureTime', formatTime(draftTime));
    setShowTimePicker(false);
  };

  const handleSubmit = async () => {
    if (!user) {
      alert(t('listNewTrip.pleaseLogInToCreateTrip'));
      navigate('/auth/login');
      return;
    }

    setIsSubmitting(true);
    try {
      const tripData = {
        tripType: 'shipping',
        originAddress: formData.originAddress,
        destinationAddress: formData.destinationAddress,
        originDetails: formData.originDetails,
        destinationDetails: formData.destinationDetails,
        originCity: formData.originDetails?.city || '',
        originCountry: formData.originDetails?.country || '',
        destinationCity: formData.destinationDetails?.city || '',
        destinationCountry: formData.destinationDetails?.country || '',
        departureDate: formData.departureDate,
        departureTime: formData.departureTime,
        deliveryMethod: formData.deliveryMethod,
        availableSpace: parseFloat(formData.availableSpace),
        spaceUnit: formData.spaceUnit,
        pricingMode: formData.pricingMode,
        pricePerUnit:
          formData.pricingMode === 'perUnit'
            ? parseFloat(formData.pricePerUnit)
            : null,
        flatPrice:
          formData.pricingMode === 'flat'
            ? parseFloat(formData.flatPrice)
            : null,
        currency: 'USD',
        notes: formData.notes,
        contactViaApp: formData.contactViaApp,
        acceptPolicy: formData.acceptPolicy,
        updatedAt: serverTimestamp(),
      };

      if (editMode && tripId) {
        // Update existing trip
        const tripRef = doc(db, 'trips', tripId);
        await updateDoc(tripRef, tripData);
        alert(t('listNewTrip.tripUpdatedSuccessfully'));
        navigate('/my-listings');
      } else {
        // Create new trip
        await addDoc(collection(db, 'trips'), {
          ...tripData,
          createdByName:
            user.displayName || user.email?.split('@')[0] || 'Traveler',
          createdByEmail: user.email || '',
          createdBy: user.uid,
          status: 'active',
          createdAt: serverTimestamp(),
        });
        alert(t('listNewTrip.tripListedSuccessfully'));
        navigate('/trips/traveler');
      }
    } catch (error) {
      console.error('Error saving trip:', error);
      alert(
        editMode
          ? t('listNewTrip.failedToUpdateTrip')
          : t('listNewTrip.failedToCreateTrip'),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const getDeliveryMethodIcon = method => {
    const methodData = deliveryMethods.find(m => m.value === method);
    return methodData ? methodData.icon : Car;
  };

  const getDeliveryMethodLabel = method => {
    const methodData = deliveryMethods.find(m => m.value === method);
    return methodData ? methodData.label : t('listNewTrip.byRoad');
  };

  const renderStep = () => {
    const step = steps[currentStep];
    if (!step) return null;

    switch (step) {
      case 'route':
        return (
          <div className="space-y-6">
            <div className="flex flex-col items-center mb-4">
              <div className="flex items-center justify-center w-24 h-24 rounded-full bg-yellow-100 mb-4">
                <Route className="h-12 w-12 text-yellow-600" />
              </div>
              <h2 className="text-2xl font-black text-gray-900 dark:text-white">
                {t('listNewTrip.routeDestination')}
              </h2>
            </div>
            <div className="space-y-4">
              <div>
                <PlacesAutocompleteInput
                  placeholder={t('listNewTrip.searchOriginAddress')}
                  onSelect={handleOriginAddressSelect}
                  onInputChange={value => {
                    if (!value) {
                      setFormData(prev => ({
                        ...prev,
                        originAddress: '',
                        originDetails: null,
                      }));
                    }
                  }}
                  value={
                    formData.originDetails
                      ? { description: formData.originAddress }
                      : null
                  }
                />
                {formData.originDetails && (
                  <div className="mt-2 flex items-center gap-2 text-sm text-gray-600 dark:text-slate-400">
                    <span>{formData.originDetails.countryFlag}</span>
                    <span>{formData.originDetails.country}</span>
                  </div>
                )}
              </div>
              <div>
                <PlacesAutocompleteInput
                  placeholder={t('listNewTrip.searchDestinationAddress')}
                  onSelect={handleDestinationAddressSelect}
                  onInputChange={value => {
                    if (!value) {
                      setFormData(prev => ({
                        ...prev,
                        destinationAddress: '',
                        destinationDetails: null,
                      }));
                    }
                  }}
                  value={
                    formData.destinationDetails
                      ? { description: formData.destinationAddress }
                      : null
                  }
                />
                {formData.destinationDetails && (
                  <div className="mt-2 flex items-center gap-2 text-sm text-gray-600 dark:text-slate-400">
                    <span>{formData.destinationDetails.countryFlag}</span>
                    <span>{formData.destinationDetails.country}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 'departure':
        return (
          <div className="space-y-6">
            <div className="flex flex-col items-center mb-4">
              <div className="flex items-center justify-center w-24 h-24 rounded-full bg-blue-100 mb-4">
                <CalendarDays className="h-12 w-12 text-blue-600" />
              </div>
              <h2 className="text-2xl font-black text-gray-900 dark:text-white">
                {t('listNewTrip.departure')}
              </h2>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 dark:text-slate-300 mb-2">
                  {t('listNewTrip.date')}
                </label>
                <button
                  type="button"
                  onClick={() => setShowDatePicker(true)}
                  className="w-full flex items-center gap-3 px-4 py-3 border-2 border-gray-300 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-800 hover:border-gray-400 dark:hover:border-slate-600 transition"
                >
                  <CalendarDays className="h-5 w-5 text-gray-500 dark:text-slate-400" />
                  <span className="flex-1 text-left text-gray-900 dark:text-white">
                    {formData.departureDate || t('listNewTrip.selectDepartureDate')}
                  </span>
                  <span className="text-gray-500 dark:text-slate-400">▼</span>
                </button>
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 dark:text-slate-300 mb-2">
                  {t('listNewTrip.time')}
                </label>
                <button
                  type="button"
                  onClick={() => setShowTimePicker(true)}
                  className="w-full flex items-center gap-3 px-4 py-3 border-2 border-gray-300 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-800 hover:border-gray-400 dark:hover:border-slate-600 transition"
                >
                  <CalendarDays className="h-5 w-5 text-gray-500 dark:text-slate-400" />
                  <span className="flex-1 text-left text-gray-900 dark:text-white">
                    {formData.departureTime || t('listNewTrip.selectDepartureTime')}
                  </span>
                  <span className="text-gray-500 dark:text-slate-400">▼</span>
                </button>
              </div>
            </div>
          </div>
        );

      case 'deliveryMethod':
        return (
          <div className="space-y-6">
            <div className="flex flex-col items-center mb-4">
              <div className="flex items-center justify-center w-24 h-24 rounded-full bg-purple-100 mb-4">
                <Truck className="h-12 w-12 text-purple-600" />
              </div>
              <h2 className="text-2xl font-black text-gray-900 dark:text-white">
                {t('listNewTrip.deliveryMethod')}
              </h2>
            </div>
            <div className="space-y-3">
              {deliveryMethods.map(method => {
                const Icon = method.icon;
                return (
                  <button
                    key={method.value}
                    type="button"
                    onClick={() =>
                      handleInputChange('deliveryMethod', method.value)
                    }
                    className={`w-full flex items-center gap-4 p-4 border-2 rounded-xl transition ${
                      formData.deliveryMethod === method.value
                        ? 'border-black dark:border-yellow-500 bg-black dark:bg-yellow-500 text-white dark:text-black'
                        : 'border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-gray-400 dark:hover:border-slate-600'
                    }`}
                  >
                    <Icon
                      className={`h-6 w-6 ${
                        formData.deliveryMethod === method.value
                          ? 'text-yellow-400 dark:text-black'
                          : 'text-gray-500 dark:text-slate-400'
                      }`}
                    />
                    <span className="flex-1 text-left font-bold dark:text-white">
                      {method.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        );

      case 'capacity':
        return (
          <div className="space-y-6">
            <div className="flex flex-col items-center mb-4">
              <div className="flex items-center justify-center w-24 h-24 rounded-full bg-green-100 mb-4">
                <Box className="h-12 w-12 text-green-600" />
              </div>
              <h2 className="text-2xl font-black text-gray-900 dark:text-white">
                {t('listNewTrip.availableSpace')}
              </h2>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 dark:text-slate-300 mb-2">
                  {t('listNewTrip.spaceNeeded')}
                </label>
                <div className="flex gap-3">
                  <div className="flex-1 flex items-center gap-3 px-4 py-3 border-2 border-gray-300 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-800">
                    <Box className="h-5 w-5 text-gray-500 dark:text-slate-400" />
                    <input
                      type="number"
                      value={formData.availableSpace}
                      onChange={e =>
                        handleInputChange('availableSpace', e.target.value)
                      }
                      placeholder={t('listNewTrip.enterAmount')}
                      className="flex-1 border-none outline-none text-gray-900 dark:text-white bg-transparent placeholder:text-gray-400 dark:placeholder:text-slate-500"
                    />
                  </div>
                  <select
                    value={formData.spaceUnit}
                    onChange={e =>
                      handleInputChange('spaceUnit', e.target.value)
                    }
                    className="px-4 py-3 border-2 border-gray-300 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-800 text-gray-900 dark:text-white font-semibold"
                  >
                    {spaceUnits.map(unit => (
                      <option key={unit.value} value={unit.value}>
                        {unit.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        );

      case 'pricing':
        return (
          <div className="space-y-6">
            <div className="flex flex-col items-center mb-4">
              <div className="flex items-center justify-center w-24 h-24 rounded-full bg-emerald-100 mb-4">
                <DollarSign className="h-12 w-12 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-black text-gray-900 dark:text-white">
                {t('listNewTrip.pricing')}
              </h2>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 dark:text-slate-300 mb-2">
                  {t('listNewTrip.pricingMode')}
                </label>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => handleInputChange('pricingMode', 'perUnit')}
                    className={`flex-1 px-4 py-3 border-2 rounded-xl font-bold transition ${
                      formData.pricingMode === 'perUnit'
                        ? 'border-black dark:border-yellow-500 bg-black dark:bg-yellow-500 text-white dark:text-black'
                        : 'border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white hover:border-gray-400 dark:hover:border-slate-600'
                    }`}
                  >
                    {t('listNewTrip.perUnit')}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleInputChange('pricingMode', 'flat')}
                    className={`flex-1 px-4 py-3 border-2 rounded-xl font-bold transition ${
                      formData.pricingMode === 'flat'
                        ? 'border-black dark:border-yellow-500 bg-black dark:bg-yellow-500 text-white dark:text-black'
                        : 'border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white hover:border-gray-400 dark:hover:border-slate-600'
                    }`}
                  >
                    {t('listNewTrip.flatPrice')}
                  </button>
                </div>
              </div>

              {formData.pricingMode === 'perUnit' ? (
                <div>
                  <label className="block text-sm font-bold text-gray-700 dark:text-slate-300 mb-2">
                    {t('listNewTrip.pricePerUnit')}
                  </label>
                  <div className="flex items-center gap-3 px-4 py-3 border-2 border-gray-300 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-800">
                    <DollarSign className="h-5 w-5 text-gray-500 dark:text-slate-400" />
                    <input
                      type="number"
                      value={formData.pricePerUnit}
                      onChange={e =>
                        handleInputChange('pricePerUnit', e.target.value)
                      }
                      placeholder={t('listNewTrip.enterPricePerUnit')}
                      className="flex-1 border-none outline-none text-gray-900 dark:text-white bg-transparent placeholder:text-gray-400 dark:placeholder:text-slate-500"
                    />
                    <span className="text-sm font-semibold text-gray-600 dark:text-slate-400">
                      USD
                    </span>
                  </div>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-bold text-gray-700 dark:text-slate-300 mb-2">
                    {t('listNewTrip.flatPriceUsd')}
                  </label>
                  <div className="flex items-center gap-3 px-4 py-3 border-2 border-gray-300 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-800">
                    <DollarSign className="h-5 w-5 text-gray-500 dark:text-slate-400" />
                    <input
                      type="number"
                      value={formData.flatPrice}
                      onChange={e =>
                        handleInputChange('flatPrice', e.target.value)
                      }
                      placeholder={t('listNewTrip.enterFlatPrice')}
                      className="flex-1 border-none outline-none text-gray-900 dark:text-white bg-transparent placeholder:text-gray-400 dark:placeholder:text-slate-500"
                    />
                    <span className="text-sm font-semibold text-gray-600 dark:text-slate-400">
                      USD
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case 'additionalInfo':
        return (
          <div className="space-y-6">
            <div className="flex flex-col items-center mb-4">
              <div className="flex items-center justify-center w-24 h-24 rounded-full bg-orange-100 mb-4">
                <FileText className="h-12 w-12 text-orange-600" />
              </div>
              <h2 className="text-2xl font-black text-gray-900 dark:text-white">
                {t('listNewTrip.additionalInformation')}
              </h2>
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 dark:text-slate-300 mb-2">
                {t('listNewTrip.notesOptional')}
              </label>
              <textarea
                value={formData.notes}
                onChange={e => handleInputChange('notes', e.target.value)}
                placeholder={t('listNewTrip.anySpecialInstructions')}
                className="w-full px-4 py-3 border-2 border-gray-300 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-800 focus:border-black dark:focus:border-yellow-500 focus:outline-none resize-none text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500"
                rows={4}
                maxLength={200}
              />
              <p className="mt-2 text-sm text-gray-500 dark:text-slate-400 text-right">
                {formData.notes.length}/200
              </p>
            </div>
          </div>
        );

      case 'agreements':
        return (
          <div className="space-y-6">
            <div className="flex flex-col items-center mb-4">
              <div className="flex items-center justify-center w-24 h-24 rounded-full bg-indigo-100 mb-4">
                <CheckCircle className="h-12 w-12 text-indigo-600" />
              </div>
              <h2 className="text-2xl font-black text-gray-900 dark:text-white">
                {t('listNewTrip.agreements')}
              </h2>
            </div>
            <div className="space-y-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.contactViaApp}
                  onChange={e =>
                    handleInputChange('contactViaApp', e.target.checked)
                  }
                  className="mt-1 w-5 h-5 rounded border-2 border-gray-300 dark:border-slate-700 text-black dark:text-yellow-500 focus:ring-2 focus:ring-black dark:focus:ring-yellow-500 bg-white dark:bg-slate-800"
                />
                <span className="text-sm text-gray-700 dark:text-slate-300">
                  {t('listNewTrip.agreeToBeContacted')}
                </span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.acceptPolicy}
                  onChange={e =>
                    handleInputChange('acceptPolicy', e.target.checked)
                  }
                  className="mt-1 w-5 h-5 rounded border-2 border-gray-300 dark:border-slate-700 text-black dark:text-yellow-500 focus:ring-2 focus:ring-black dark:focus:ring-yellow-500 bg-white dark:bg-slate-800"
                />
                <span className="text-sm text-gray-700 dark:text-slate-300">
                  {t('listNewTrip.acceptTerms')}
                </span>
              </label>
            </div>
          </div>
        );

      case 'summary':
        const DeliveryMethodIcon = getDeliveryMethodIcon(
          formData.deliveryMethod,
        );
        return (
          <div className="space-y-6">
            <div className="flex flex-col items-center mb-4">
              <div className="flex items-center justify-center w-24 h-24 rounded-full bg-gray-100 mb-4">
                <ListChecks className="h-12 w-12 text-gray-600" />
              </div>
              <h2 className="text-2xl font-black text-gray-900 dark:text-white">
                {t('listNewTrip.summary')}
              </h2>
            </div>
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 dark:bg-slate-800 rounded-xl">
                <p className="text-xs font-bold uppercase text-gray-500 dark:text-slate-400 mb-1">
                  {t('listNewTrip.route')}
                </p>
                <p className="text-base font-bold text-gray-900 dark:text-white">
                  {formData.originAddress || '-'} →{' '}
                  {formData.destinationAddress || '-'}
                </p>
              </div>
              <div className="p-4 bg-gray-50 dark:bg-slate-800 rounded-xl">
                <p className="text-xs font-bold uppercase text-gray-500 dark:text-slate-400 mb-1">
                  {t('listNewTrip.departure')}
                </p>
                <p className="text-base font-bold text-gray-900 dark:text-white">
                  {formData.departureDate && formData.departureTime
                    ? `${formData.departureDate} ${t('common.at')} ${formData.departureTime}`
                    : '-'}
                </p>
              </div>
              <div className="p-4 bg-gray-50 dark:bg-slate-800 rounded-xl">
                <p className="text-xs font-bold uppercase text-gray-500 dark:text-slate-400 mb-1">
                  {t('listNewTrip.deliveryMethod')}
                </p>
                <div className="flex items-center gap-2">
                  <DeliveryMethodIcon className="h-5 w-5 text-gray-600 dark:text-slate-400" />
                  <p className="text-base font-bold text-gray-900 dark:text-white">
                    {getDeliveryMethodLabel(formData.deliveryMethod, t)}
                  </p>
                </div>
              </div>
              <div className="p-4 bg-gray-50 dark:bg-slate-800 rounded-xl">
                <p className="text-xs font-bold uppercase text-gray-500 dark:text-slate-400 mb-1">
                  {t('listNewTrip.availableSpace')}
                </p>
                <p className="text-base font-bold text-gray-900 dark:text-white">
                  {formData.availableSpace
                    ? `${formData.availableSpace} ${formData.spaceUnit}`
                    : '-'}
                </p>
              </div>
              <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-200 dark:border-yellow-800 rounded-xl">
                <p className="text-xs font-bold uppercase text-yellow-700 dark:text-yellow-300 mb-1">
                  {t('listNewTrip.pricing')}
                </p>
                <p className="text-lg font-black text-yellow-900 dark:text-yellow-200">
                  {formData.pricingMode === 'perUnit'
                    ? `USD ${formData.pricePerUnit || 0}/${formData.spaceUnit}`
                    : `USD ${formData.flatPrice || 0} flat`}
                </p>
              </div>
              {formData.notes && (
                <div className="p-4 bg-gray-50 dark:bg-slate-800 rounded-xl">
                  <p className="text-xs font-bold uppercase text-gray-500 dark:text-slate-400 mb-1">
                    {t('listNewTrip.notes')}
                  </p>
                  <p className="text-sm text-gray-700 dark:text-slate-300">
                    {formData.notes}
                  </p>
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-slate-950 dark:to-slate-900"
      lang="en"
    >
      <MainNavbar />
      <section className="px-4 pt-24 pb-12 sm:px-6 lg:px-8">
        <div className="rounded-3xl bg-gradient-to-br from-yellow-50 via-white to-white p-8 shadow-xl dark:from-slate-900/40 dark:via-slate-900 dark:to-slate-900 lg:p-12">
          <div className="grid gap-10 lg:grid-cols-[1.2fr_0.8fr] lg:items-center">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.5em] text-yellow-500">
                {editMode ? t('listNewTrip.editTrip') : t('listNewTrip.listNewTrip')}
              </p>
              <h1 className="mt-4 text-4xl font-black text-gray-900 dark:text-white">
                {editMode
                  ? t('listNewTrip.updateTripDetails')
                  : t('listNewTrip.turnSpareLuggageSpace')}
              </h1>
              <p className="mt-4 text-base text-gray-600 dark:text-slate-300">
                {t('listNewTrip.publishRouteDescription')}
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <span className="inline-flex items-center gap-2 rounded-full bg-white/90 px-3 py-1 text-sm font-semibold text-gray-700 dark:bg-white/10 dark:text-slate-100">
                  <Plane className="h-4 w-4 text-yellow-500" />
                  {t('listNewTrip.globalAirportNetwork')}
                </span>
                <span className="inline-flex items-center gap-2 rounded-full bg-white/90 px-3 py-1 text-sm font-semibold text-gray-700 dark:bg-white/10 dark:text-slate-100">
                  <DollarSign className="h-4 w-4 text-yellow-500" />
                  {t('listNewTrip.escrowSecuredPayouts')}
                </span>
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => navigate('/trips/traveler')}
                  className="rounded-2xl border-2 border-black px-5 py-3 text-sm font-bold text-black transition hover:bg-black hover:text-white dark:border-white dark:text-white dark:hover:bg-white dark:hover:text-black"
                >
                  {t('listNewTrip.viewMyListings')}
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/trips/sender')}
                  className="rounded-2xl border-2 border-gray-300 px-5 py-3 text-sm font-bold text-gray-700 transition hover:bg-gray-100 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  {t('listNewTrip.checkSenderRequests')}
                </button>
              </div>
            </div>
            <div className="grid gap-4 rounded-3xl bg-white/80 p-6 shadow-inner dark:bg-white/5 sm:grid-cols-3">
              {tripHeroHighlights.map(item => (
                <div
                  key={item.label}
                  className="rounded-2xl border border-yellow-100 bg-white p-4 text-center dark:border-white/10 dark:bg-white/5"
                >
                  <p className="text-2xl font-black text-gray-900 dark:text-white">
                    {item.value}
                  </p>
                  <p className="mt-1 text-xs font-semibold uppercase tracking-widest text-gray-500 dark:text-slate-400">
                    {item.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      <main className="mx-auto max-w-4xl px-4 pt-6 pb-16 sm:px-6 lg:px-8">
        {/* Progress Indicator */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-gray-600 dark:text-slate-400">
              {t('listNewTrip.stepOf', { current: currentStep + 1, total: totalSteps })}
            </span>
          </div>
          <div className="h-2 bg-gray-200 dark:bg-slate-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-black dark:bg-yellow-500 transition-all duration-300"
              style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
            />
          </div>
        </div>

        {/* Form Content */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl p-8 shadow-lg border-2 border-gray-200 dark:border-slate-800 mb-6">
          {renderStep()}
        </div>

        {/* Navigation Buttons */}
        <div className="flex gap-4">
          {currentStep > 0 && (
            <button
              type="button"
              onClick={prevStep}
              disabled={isSubmitting}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-4 bg-white dark:bg-slate-800 border-2 border-black dark:border-slate-200 rounded-xl font-bold text-black dark:text-white hover:bg-gray-50 dark:hover:bg-slate-700 transition disabled:opacity-50"
            >
              <ArrowLeft className="h-5 w-5" />
              {t('listNewTrip.back')}
            </button>
          )}

          {currentStep < totalSteps - 1 ? (
            <button
              type="button"
              onClick={nextStep}
              disabled={isSubmitting}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-4 bg-black dark:bg-yellow-500 rounded-xl font-bold text-yellow-500 dark:text-black hover:bg-gray-900 dark:hover:bg-yellow-400 transition disabled:opacity-50"
            >
              {t('listNewTrip.next')}
              <ArrowRight className="h-5 w-5" />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="flex-1 px-6 py-4 bg-black rounded-xl font-bold text-yellow-500 hover:bg-gray-900 transition disabled:opacity-50"
            >
              {isSubmitting
                ? editMode
                  ? t('listNewTrip.updating')
                  : t('listNewTrip.creating')
                : editMode
                ? t('listNewTrip.updateTrip')
                : t('listNewTrip.submitRequest')}
            </button>
          )}
        </div>
      </main>

      {showDatePicker && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="w-full max-w-xl rounded-3xl bg-white p-6 shadow-2xl dark:bg-slate-900">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-gray-500 dark:text-slate-400">
                  {t('listNewTrip.departureDate')}
                </p>
                <h3 className="mt-1 text-2xl font-black text-gray-900 dark:text-white">
                  {t('listNewTrip.pickWhenYouWillTravel')}
                </h3>
                <p className="text-sm text-gray-500 dark:text-slate-400">
                  {t('listNewTrip.quickSuggestionsKeepItSimple')}
                </p>
              </div>
            </div>
            <div className="mt-6 space-y-6">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gray-500 dark:text-slate-400">
                  {t('listNewTrip.quickPicks')}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {quickDateOptions.map(option => {
                    const optionDate = new Date();
                    optionDate.setDate(
                      optionDate.getDate() + option.offsetDays,
                    );
                    return (
                      <button
                        key={option.label}
                        type="button"
                        onClick={() => handleQuickDateSelect(option.offsetDays)}
                        className={`rounded-2xl px-4 py-2 text-sm font-semibold transition ${
                          isSameDay(draftDate, optionDate)
                            ? 'bg-black text-yellow-400 dark:bg-yellow-500 dark:text-black'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700'
                        }`}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gray-500 dark:text-slate-400">
                  {t('listNewTrip.calendar')}
                </p>
                <div className="mt-3 rounded-2xl border-2 border-dashed border-gray-200 p-3 dark:border-slate-700">
                  <input
                    type="date"
                    lang="en"
                    value={draftDate.toISOString().split('T')[0]}
                    min={new Date().toISOString().split('T')[0]}
                    onChange={event =>
                      handleDateInputChange(event.target.value)
                    }
                    className="w-full rounded-xl border-2 border-gray-200 px-4 py-3 text-gray-900 focus:border-black focus:outline-none dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                  />
                </div>
              </div>
            </div>
            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={() => setShowDatePicker(false)}
                className="flex-1 rounded-2xl border-2 border-gray-300 px-4 py-3 font-semibold text-gray-900 transition hover:border-gray-400 dark:border-slate-700 dark:text-slate-100"
              >
                {t('listNewTrip.cancel')}
              </button>
              <button
                type="button"
                onClick={confirmDateSelection}
                className="flex-1 rounded-2xl bg-black px-4 py-3 font-semibold text-yellow-400 transition hover:bg-gray-900 dark:bg-yellow-500 dark:text-black dark:hover:bg-yellow-400"
              >
                {t('listNewTrip.useThisDate')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showTimePicker && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="w-full max-w-xl rounded-3xl bg-white p-6 shadow-2xl dark:bg-slate-900">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-gray-500 dark:text-slate-400">
                {t('listNewTrip.departureTime')}
              </p>
              <h3 className="mt-1 text-2xl font-black text-gray-900 dark:text-white">
                {t('listNewTrip.setClearTakeoffTime')}
              </h3>
              <p className="text-sm text-gray-500 dark:text-slate-400">
                {t('listNewTrip.tapHourChooseMinutes')}
              </p>
            </div>
            <div className="mt-6 space-y-6">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gray-500 dark:text-slate-400">
                  {t('listNewTrip.quickPicks')}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {quickTimeOptions.map(option => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => handleQuickTimeSelect(option)}
                      className={`rounded-2xl px-4 py-2 text-sm font-semibold transition ${
                        option === formatTime(draftTime)
                          ? 'bg-black text-yellow-400 dark:bg-yellow-500 dark:text-black'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700'
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gray-500 dark:text-slate-400">
                  {t('listNewTrip.manualAdjust')}
                </p>
                <div className="mt-3 grid gap-4 sm:grid-cols-2">
                  <div className="rounded-3xl border-2 border-gray-200 p-4 text-center dark:border-slate-700">
                    <p className="text-xs font-semibold uppercase tracking-[0.3em] text-gray-500 dark:text-slate-400">
                      {t('listNewTrip.hour')}
                    </p>
                    <div className="mt-2 flex flex-col items-center gap-3">
                      <button
                        type="button"
                        onClick={() => adjustHour(1)}
                        className="rounded-full border border-gray-300 p-2 text-gray-700 transition hover:border-gray-500 dark:border-slate-600 dark:text-slate-100"
                        aria-label="Increase hour"
                      >
                        <ChevronUp className="h-5 w-5" />
                      </button>
                      <p className="text-5xl font-black text-gray-900 dark:text-white">
                        {draftTime.getHours().toString().padStart(2, '0')}
                      </p>
                      <button
                        type="button"
                        onClick={() => adjustHour(-1)}
                        className="rounded-full border border-gray-300 p-2 text-gray-700 transition hover:border-gray-500 dark:border-slate-600 dark:text-slate-100"
                        aria-label="Decrease hour"
                      >
                        <ChevronDown className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                  <div className="rounded-3xl border-2 border-gray-200 p-4 text-center dark:border-slate-700">
                    <p className="text-xs font-semibold uppercase tracking-[0.3em] text-gray-500 dark:text-slate-400">
                      {t('listNewTrip.minutes')}
                    </p>
                    <p className="text-[11px] text-gray-500 dark:text-slate-400">
                      {t('listNewTrip.adjustsInMinuteSteps', { step: minuteStep })}
                    </p>
                    <div className="mt-2 flex flex-col items-center gap-3">
                      <button
                        type="button"
                        onClick={() => adjustMinute(1)}
                        className="rounded-full border border-gray-300 p-2 text-gray-700 transition hover:border-gray-500 dark:border-slate-600 dark:text-slate-100"
                        aria-label="Increase minutes"
                      >
                        <ChevronUp className="h-5 w-5" />
                      </button>
                      <p className="text-5xl font-black text-gray-900 dark:text-white">
                        {draftTime.getMinutes().toString().padStart(2, '0')}
                      </p>
                      <button
                        type="button"
                        onClick={() => adjustMinute(-1)}
                        className="rounded-full border border-gray-300 p-2 text-gray-700 transition hover:border-gray-500 dark:border-slate-600 dark:text-slate-100"
                        aria-label="Decrease minutes"
                      >
                        <ChevronDown className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={() => setShowTimePicker(false)}
                className="flex-1 rounded-2xl border-2 border-gray-300 px-4 py-3 font-semibold text-gray-900 transition hover:border-gray-400 dark:border-slate-700 dark:text-slate-100"
              >
                {t('listNewTrip.cancel')}
              </button>
              <button
                type="button"
                onClick={confirmTimeSelection}
                className="flex-1 rounded-2xl bg-black px-4 py-3 font-semibold text-yellow-400 transition hover:bg-gray-900 dark:bg-yellow-500 dark:text-black dark:hover:bg-yellow-400"
              >
                {t('listNewTrip.useThisTime')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
